import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  AlertCircle,
  Cable,
  Check,
  ChevronDown,
  Radio,
  RefreshCw,
  Router,
  Search,
  Signal,
  Users,
  X,
} from 'lucide-react';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import SinalCtoMap from './SinalCtoMap';
import SinalCtoClientesPanel from './SinalCtoClientesPanel';
import { SinalCtoDataProvider, useSinalCtoData } from '../context/SinalCtoDataContext';
import {
  buildOptionsFromRows,
  buildSignalDashboardSnapshot,
  applySignalFilters,
  normalizeSignalFilterLabel,
  SINAL_FILTER_FIELDS,
  SIGNAL_CLASSIFICATION_ORDER,
} from '../utils/sinalCtoAnalytics';

const MAP_SYNC_DEBOUNCE_MS = 180;
const panelClassName =
  'rounded-[24px] border border-[color:var(--border)] bg-[color:var(--surface)] p-4 shadow-[0_16px_40px_rgba(15,23,42,0.10)]';
const chartTooltipStyle = {
  background: 'var(--surface-strong)',
  border: '1px solid var(--border)',
  borderRadius: '12px',
  color: 'var(--text-primary)',
  boxShadow: '0 18px 40px rgba(15,23,42,0.12)',
};

const integerFormatter = new Intl.NumberFormat('pt-BR');
const compactFormatter = new Intl.NumberFormat('pt-BR', {
  notation: 'compact',
  maximumFractionDigits: 2,
});
const rxFormatter = new Intl.NumberFormat('pt-BR', {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

const classificationOptions = SIGNAL_CLASSIFICATION_ORDER.map((classification) => ({
  value: classification,
  label: classification,
}));

const getActiveFilterValues = (filters, field) => filters
  .filter((filter) => filter.field === field)
  .map((filter) => String(filter.value));

const buildFieldRowsWithoutFilter = (rows, filters, field, applyFilters) => applyFilters(
  rows,
  filters.filter((filter) => filter.field !== field),
);

const SimpleFilterSelect = ({ label, value, options, onChange, displayLabel, helperText }) => {
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef(null);
  const normalizedOptions = useMemo(
    () => options.map((option) => (
      typeof option === 'object' && option !== null
        ? option
        : { value: option, label: option }
    )),
    [options],
  );
  const allOptions = useMemo(() => [{ value: '', label: 'Todos' }, ...normalizedOptions], [normalizedOptions]);
  const selectedOption = normalizedOptions.find((option) => String(option.value) === String(value))
    || { value: '', label: 'Todos' };

  useEffect(() => {
    if (!isOpen) {
      return undefined;
    }

    const handlePointerDown = (event) => {
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    const handleEscape = (event) => {
      if (event.key === 'Escape') {
        setIsOpen(false);
      }
    };

    document.addEventListener('pointerdown', handlePointerDown);
    document.addEventListener('keydown', handleEscape);

    return () => {
      document.removeEventListener('pointerdown', handlePointerDown);
      document.removeEventListener('keydown', handleEscape);
    };
  }, [isOpen]);

  return (
    <div ref={containerRef} className="relative flex min-w-0 flex-col gap-2">
      <span className="text-[11px] font-semibold uppercase tracking-[0.16em] text-[color:var(--text-muted)]">
        {label}
      </span>
      <button
        type="button"
        onClick={() => setIsOpen((current) => !current)}
        className={`group relative flex min-h-[44px] w-full items-center justify-between gap-3 overflow-hidden rounded-[18px] border px-4 py-3 text-left transition-all duration-200 ${
          isOpen
            ? 'border-[color:var(--accent)] bg-[color:var(--surface-strong)]'
            : 'border-[color:var(--header-control-border-subtle)] bg-[color:var(--surface-strong)]/92 hover:border-[color:var(--accent)]/40 hover:bg-[color:var(--surface-muted)]/72'
        }`}
        aria-haspopup="listbox"
        aria-expanded={isOpen}
      >
        <div className="min-w-0">
          <div className="truncate text-sm font-medium text-[color:var(--text-primary)]">
            {displayLabel || selectedOption.label}
          </div>
          <div className="mt-0.5 text-[10px] uppercase tracking-[0.12em] text-[color:var(--text-muted)]">
            {helperText || (value ? 'Filtro ativo' : 'Todos os registros')}
          </div>
        </div>
        <span className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-full border border-[color:var(--header-control-border-subtle)] bg-[color:var(--surface-muted)]/68 text-[color:var(--text-muted)] transition-colors duration-200 group-hover:text-[color:var(--accent)]">
          <ChevronDown className={`h-4 w-4 transition-transform duration-200 ${isOpen ? 'rotate-180 text-[color:var(--accent)]' : ''}`} />
        </span>
      </button>

      {isOpen ? (
        <div className="absolute left-0 right-0 top-[calc(100%+10px)] z-30 overflow-hidden rounded-[20px] border border-[color:var(--header-control-border-subtle)] bg-[color:var(--surface-strong)]/96 p-2 shadow-[0_28px_60px_rgba(2,6,23,0.26)] backdrop-blur-xl">
          <div role="listbox" aria-label={label} className="app-scroll-subtle max-h-[280px] space-y-1 overflow-y-auto pr-1">
            {allOptions.map((option) => {
              const isSelected = String(option.value) === String(value);

              return (
                <button
                  key={`${label}-${option.value || 'all'}`}
                  type="button"
                  role="option"
                  aria-selected={isSelected}
                  onClick={() => {
                    onChange(option.value);
                    setIsOpen(false);
                  }}
                  className={`flex w-full items-center justify-between gap-3 rounded-[14px] px-3 py-2.5 text-left transition-all duration-150 ${
                    isSelected
                      ? 'bg-[color:var(--surface-muted)] text-[color:var(--text-primary)]'
                      : 'text-[color:var(--text-primary)] hover:bg-[color:var(--surface-muted)]/72'
                  }`}
                >
                  <span className="truncate text-sm font-medium">{option.label}</span>
                  <span className={`inline-flex h-6 w-6 shrink-0 items-center justify-center rounded-full border ${
                    isSelected
                      ? 'border-[color:var(--accent)] bg-[color:var(--accent)]/12 text-[color:var(--accent)]'
                      : 'border-[color:var(--header-control-border-subtle)] text-transparent'
                  }`}>
                    <Check className="h-3.5 w-3.5" />
                  </span>
                </button>
              );
            })}
          </div>
        </div>
      ) : null}
    </div>
  );
};

const KpiCard = ({ label, value, hint, icon: Icon, accent, warning }) => (
  <div className={`${panelClassName} relative min-h-[138px] overflow-hidden`}>
    <div className="pointer-events-none absolute inset-x-5 top-0 h-px" style={{ background: accent }} />
    <div className="flex items-start justify-between gap-3">
      <div className="min-w-0">
        <p className="text-[11px] font-semibold uppercase tracking-[0.12em] text-[color:var(--text-muted)]">{label}</p>
        <p className="mt-3 truncate text-3xl font-semibold leading-none text-[color:var(--text-primary)] sm:text-4xl">
          {value}
        </p>
        <p className={`mt-2 text-xs ${warning ? 'text-amber-500 dark:text-amber-300' : 'text-[color:var(--text-muted)]'}`}>{hint}</p>
      </div>
      <span className="inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl border border-[color:var(--header-control-border-subtle)] bg-[color:var(--surface-muted)]/72 text-[color:var(--accent)]">
        <Icon className="h-5 w-5" />
      </span>
    </div>
  </div>
);

const SourceErrorNotice = ({ message, onRetry }) => {
  if (!message) {
    return null;
  }

  return (
    <div className="flex flex-col gap-3 rounded-[18px] border border-amber-400/30 bg-amber-500/10 px-4 py-3 text-sm text-amber-700 dark:text-amber-200 sm:flex-row sm:items-center sm:justify-between">
      <div className="flex items-start gap-2">
        <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
        <span>{message}</span>
      </div>
      <button
        type="button"
        onClick={onRetry}
        className="inline-flex min-h-[36px] items-center justify-center gap-2 rounded-full border border-amber-400/40 px-3 py-1.5 text-xs font-semibold transition-colors hover:bg-amber-500/10"
      >
        <RefreshCw className="h-3.5 w-3.5" />
        Tentar novamente
      </button>
    </div>
  );
};

const AverageRxTooltip = ({ active, payload, label }) => {
  if (!active || !payload || !payload.length) {
    return null;
  }

  const row = payload[0]?.payload;

  return (
    <div style={chartTooltipStyle} className="min-w-[220px] p-3">
      <p className="text-sm font-semibold text-[color:var(--text-primary)]">{label}</p>
      <div className="mt-2 space-y-1 text-xs text-[color:var(--text-muted)]">
        <p>CTOs com sinal: {integerFormatter.format(row.count || 0)}</p>
        <p>Média RX: {rxFormatter.format(Number(row.media_rx || 0))} dBm</p>
      </div>
    </div>
  );
};

const SinalCtoDashboardContent = ({ isMobile = false }) => {
  const {
    sinalRowsRaw,
    rowsEnriched,
    rowsFiltered,
    qtdCabo,
    filters,
    loading,
    sourceErrors,
    reload,
    setFieldFilter,
    toggleFilter,
    removeFilter,
    clearFilters,
  } = useSinalCtoData();
  const [mapRows, setMapRows] = useState(rowsFiltered);
  const [isMapSyncing, setIsMapSyncing] = useState(false);
  const [popSearch, setPopSearch] = useState('');
  const [citySearch, setCitySearch] = useState('');
  const [ctoSelecionada, setCtoSelecionada] = useState(null);

  useEffect(() => {
    if (mapRows === rowsFiltered) {
      setIsMapSyncing(false);
      return undefined;
    }

    setIsMapSyncing(true);

    let debounceId = 0;
    let idleId = 0;
    let cancelled = false;

    const commitRows = () => {
      if (cancelled) {
        return;
      }

      setMapRows(rowsFiltered);
      setIsMapSyncing(false);
    };

    const scheduleCommit = () => {
      if (typeof window !== 'undefined' && 'requestIdleCallback' in window) {
        idleId = window.requestIdleCallback(commitRows, { timeout: 450 });
        return;
      }

      idleId = window.setTimeout(commitRows, 0);
    };

    debounceId = window.setTimeout(scheduleCommit, MAP_SYNC_DEBOUNCE_MS);

    return () => {
      cancelled = true;
      window.clearTimeout(debounceId);

      if (typeof window !== 'undefined' && 'cancelIdleCallback' in window) {
        window.cancelIdleCallback(idleId);
      } else {
        window.clearTimeout(idleId);
      }
    };
  }, [mapRows, rowsFiltered]);

  const activeFiltersByField = useMemo(
    () => filters.reduce((acc, filter) => {
      if (!acc[filter.field]) {
        acc[filter.field] = [];
      }

      acc[filter.field].push(String(filter.value));
      return acc;
    }, {}),
    [filters],
  );

  const activeCities = useMemo(() => getActiveFilterValues(filters, SINAL_FILTER_FIELDS.cidade), [filters]);
  const activeProjects = useMemo(() => getActiveFilterValues(filters, SINAL_FILTER_FIELDS.projeto), [filters]);
  const activePops = useMemo(() => getActiveFilterValues(filters, SINAL_FILTER_FIELDS.pop), [filters]);
  const activeClassifications = useMemo(() => getActiveFilterValues(filters, SINAL_FILTER_FIELDS.classificacao_sinal), [filters]);

  const rowsWithoutCidadeFilter = useMemo(
    () => buildFieldRowsWithoutFilter(rowsEnriched, filters, SINAL_FILTER_FIELDS.cidade, applySignalFilters),
    [filters, rowsEnriched],
  );
  const rowsWithoutProjetoFilter = useMemo(
    () => buildFieldRowsWithoutFilter(rowsEnriched, filters, SINAL_FILTER_FIELDS.projeto, applySignalFilters),
    [filters, rowsEnriched],
  );
  const rowsWithoutPopFilter = useMemo(
    () => buildFieldRowsWithoutFilter(rowsEnriched, filters, SINAL_FILTER_FIELDS.pop, applySignalFilters),
    [filters, rowsEnriched],
  );

  const cidadeOptions = useMemo(
    () => buildOptionsFromRows(rowsWithoutCidadeFilter, SINAL_FILTER_FIELDS.cidade),
    [rowsWithoutCidadeFilter],
  );
  const projetoOptions = useMemo(
    () => buildOptionsFromRows(rowsWithoutProjetoFilter, SINAL_FILTER_FIELDS.projeto),
    [rowsWithoutProjetoFilter],
  );
  const popOptions = useMemo(
    () => buildOptionsFromRows(rowsWithoutPopFilter, SINAL_FILTER_FIELDS.pop),
    [rowsWithoutPopFilter],
  );

  const snapshot = useMemo(
    () => buildSignalDashboardSnapshot(rowsFiltered, qtdCabo, activeProjects),
    [activeProjects, qtdCabo, rowsFiltered],
  );

  const classificationTotal = useMemo(
    () => snapshot.classificationSummary.reduce((total, item) => total + item.count, 0),
    [snapshot.classificationSummary],
  );
  const popAverageRows = useMemo(() => {
    const search = popSearch.trim().toLocaleLowerCase('pt-BR');

    if (!search) {
      return snapshot.averages.pop;
    }

    return snapshot.averages.pop.filter((row) => (
      String(row.label || '').toLocaleLowerCase('pt-BR').includes(search)
    ));
  }, [popSearch, snapshot.averages.pop]);
  const cityAverageRows = useMemo(() => {
    const search = citySearch.trim().toLocaleLowerCase('pt-BR');

    if (!search) {
      return snapshot.averages.cidade;
    }

    return snapshot.averages.cidade.filter((row) => (
      String(row.label || '').toLocaleLowerCase('pt-BR').includes(search)
    ));
  }, [citySearch, snapshot.averages.cidade]);

  const topKpis = useMemo(() => [
    {
      label: 'Quantidade de CTOs',
      value: integerFormatter.format(snapshot.summary.quantidadeCtos),
      icon: Router,
      hint: 'CTOs distintas com sinal no recorte',
    },
    {
      label: 'Quantidade de cabo',
      value: compactFormatter.format(snapshot.summary.quantidadeCabo),
      icon: Cable,
      hint: snapshot.summary.caboFiltradoPorProjeto ? 'Filtrado por projeto' : 'Total geral de cabo',
      warning: activeProjects.length > 0 && !snapshot.summary.caboFiltradoPorProjeto,
    },
    {
      label: 'Média RX geral',
      value: rxFormatter.format(snapshot.summary.mediaRxGeral),
      icon: Signal,
      hint: 'Média simples das CTOs com sinal',
    },
    {
      label: 'Média TX geral',
      value: rxFormatter.format(snapshot.summary.mediaTxGeral),
      icon: Signal,
      hint: 'Média simples de TX das CTOs com sinal',
    },
    {
      label: 'Clientes analisados',
      value: integerFormatter.format(snapshot.summary.quantidadeClientes),
      icon: Users,
      hint: 'Soma dos clientes informados por CTO',
    },
  ], [activeProjects.length, snapshot]);

  if (loading && sinalRowsRaw.length === 0) {
    return (
      <div className="mt-4 space-y-4">
        <div className={`${panelClassName} h-[184px] animate-pulse bg-[color:var(--surface-strong)]`} />
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 2xl:grid-cols-5">
          {Array.from({ length: 5 }).map((_, index) => (
            <div key={`sinal-kpi-skeleton-${index}`} className={`${panelClassName} h-[136px] animate-pulse bg-[color:var(--surface-strong)]`} />
          ))}
        </div>
        <div className="grid grid-cols-1 gap-4 xl:grid-cols-12">
          <div className="xl:col-span-7">
            <SinalCtoMap rows={[]} loading />
          </div>
          <div className="space-y-4 xl:col-span-5">
            <div className={`${panelClassName} h-[280px] animate-pulse bg-[color:var(--surface-strong)]`} />
            <div className={`${panelClassName} h-[280px] animate-pulse bg-[color:var(--surface-strong)]`} />
          </div>
        </div>
      </div>
    );
  }

  if (sourceErrors.sinal) {
    return (
      <div className="mt-4">
        <div className={`${panelClassName} flex min-h-[240px] flex-col items-center justify-center gap-3 text-center`}>
          <AlertCircle className="h-8 w-8 text-rose-400" />
          <div>
            <p className="text-sm font-semibold text-[color:var(--text-primary)]">Falha ao carregar Sinal de CTO</p>
            <p className="mt-1 text-sm text-[color:var(--text-muted)]">{sourceErrors.sinal}</p>
          </div>
          <button
            type="button"
            onClick={reload}
            className="inline-flex min-h-[40px] items-center gap-2 rounded-full border border-[color:var(--header-control-border-subtle)] px-4 py-2 text-sm font-semibold text-[color:var(--text-primary)] transition-colors hover:bg-[color:var(--surface-muted)]"
          >
            <RefreshCw className="h-4 w-4 text-[color:var(--accent)]" />
            Tentar novamente
          </button>
        </div>
      </div>
    );
  }

  if (!loading && sinalRowsRaw.length === 0) {
    return (
      <div className="mt-4">
        <div className={`${panelClassName} flex min-h-[260px] flex-col items-center justify-center gap-3 text-center`}>
          <Radio className="h-8 w-8 text-[color:var(--text-muted)]" />
          <div>
            <p className="text-sm font-semibold text-[color:var(--text-primary)]">Nenhuma CTO com sinal encontrada</p>
            <p className="mt-1 text-sm text-[color:var(--text-muted)]">A API respondeu sem registros para a action `sinal_ctos`.</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="mt-4 space-y-4">
      <SourceErrorNotice message={sourceErrors.ocupacao} onRetry={reload} />
      <SourceErrorNotice message={sourceErrors.cabo} onRetry={reload} />

      <div className={`${panelClassName} flex flex-col gap-4 overflow-visible`}>
        <div className="flex flex-col gap-2 lg:flex-row lg:items-end lg:justify-between">
          <div>
            <h2 className="text-sm font-semibold text-[color:var(--text-primary)]">Filtros locais de sinal</h2>
            <p className="mt-1 text-xs text-[color:var(--text-muted)]">
              Cidade, projeto e POP vêm da ocupação de rede. CTOs sem vínculo permanecem no total geral e no mapa sem filtros dimensionais.
            </p>
            <p className="mt-2 text-[11px] uppercase tracking-[0.12em] text-[color:var(--text-muted)]">
              Projeto só afeta o cabo quando o backend entrega detalhamento por projeto.
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-2 text-xs text-[color:var(--text-muted)]">
            <span className="rounded-full border border-[color:var(--header-control-border-subtle)] px-2.5 py-1">
              {`${integerFormatter.format(rowsFiltered.length)} CTOs visíveis`}
            </span>
            <span className="rounded-full border border-[color:var(--header-control-border-subtle)] px-2.5 py-1">
              {`${integerFormatter.format(sinalRowsRaw.length)} CTOs no sinal`}
            </span>
          </div>
        </div>

        <div className="grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-4">
          <SimpleFilterSelect
            label="Cidade"
            value={(activeFiltersByField[SINAL_FILTER_FIELDS.cidade] && activeFiltersByField[SINAL_FILTER_FIELDS.cidade].length === 1) ? activeFiltersByField[SINAL_FILTER_FIELDS.cidade][0] : ''}
            displayLabel={activeCities.length > 1 ? `${activeCities.length} cidades ativas` : undefined}
            helperText={activeCities.length > 1 ? 'Filtros combinados' : undefined}
            options={cidadeOptions}
            onChange={(value) => setFieldFilter(SINAL_FILTER_FIELDS.cidade, value)}
          />
          <SimpleFilterSelect
            label="Projeto"
            value={(activeFiltersByField[SINAL_FILTER_FIELDS.projeto] && activeFiltersByField[SINAL_FILTER_FIELDS.projeto].length === 1) ? activeFiltersByField[SINAL_FILTER_FIELDS.projeto][0] : ''}
            displayLabel={activeProjects.length > 1 ? `${activeProjects.length} projetos ativos` : undefined}
            helperText={activeProjects.length > 1 ? 'Filtros combinados' : undefined}
            options={projetoOptions}
            onChange={(value) => setFieldFilter(SINAL_FILTER_FIELDS.projeto, value)}
          />
          <SimpleFilterSelect
            label="POP"
            value={(activeFiltersByField[SINAL_FILTER_FIELDS.pop] && activeFiltersByField[SINAL_FILTER_FIELDS.pop].length === 1) ? activeFiltersByField[SINAL_FILTER_FIELDS.pop][0] : ''}
            displayLabel={activePops.length > 1 ? `${activePops.length} POPs ativos` : undefined}
            helperText={activePops.length > 1 ? 'Filtros combinados' : undefined}
            options={popOptions}
            onChange={(value) => setFieldFilter(SINAL_FILTER_FIELDS.pop, value)}
          />
          <SimpleFilterSelect
            label="Classificação"
            value={(activeFiltersByField[SINAL_FILTER_FIELDS.classificacao_sinal] && activeFiltersByField[SINAL_FILTER_FIELDS.classificacao_sinal].length === 1) ? activeFiltersByField[SINAL_FILTER_FIELDS.classificacao_sinal][0] : ''}
            displayLabel={activeClassifications.length > 1 ? `${activeClassifications.length} classes ativas` : undefined}
            helperText={activeClassifications.length > 1 ? 'Filtros combinados' : undefined}
            options={classificationOptions}
            onChange={(value) => setFieldFilter(SINAL_FILTER_FIELDS.classificacao_sinal, value)}
          />
        </div>

        {filters.length > 0 ? (
          <div className="flex flex-wrap items-center gap-2">
            {filters.map((filter) => (
              <button
                key={`${filter.field}-${filter.value}`}
                type="button"
                onClick={() => removeFilter(filter)}
                className="inline-flex min-h-[36px] items-center gap-2 rounded-full border border-[color:var(--header-control-border-subtle)] bg-[color:var(--surface-muted)]/72 px-3 py-1.5 text-xs font-medium text-[color:var(--text-primary)] transition-colors hover:bg-[color:var(--surface-muted)]"
              >
                <span>{filter.label || normalizeSignalFilterLabel(filter.field, filter.value)}</span>
                <X className="h-3.5 w-3.5 text-[color:var(--text-muted)]" />
              </button>
            ))}
            <button
              type="button"
              onClick={clearFilters}
              className="inline-flex min-h-[36px] items-center rounded-full border border-[color:var(--header-control-border-subtle)] px-3 py-1.5 text-xs font-semibold text-[color:var(--text-muted)] transition-colors hover:text-[color:var(--text-primary)]"
            >
              Limpar filtros
            </button>
          </div>
        ) : null}
      </div>

      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 2xl:grid-cols-5">
        {topKpis.map((kpi, index) => (
          <KpiCard
            key={kpi.label}
            label={kpi.label}
            value={kpi.value}
            hint={kpi.warning ? 'Projeto ativo, mas sem detalhamento por projeto no cabo' : kpi.hint}
            icon={kpi.icon}
            warning={kpi.warning}
            accent={[
              'linear-gradient(90deg, transparent, #2563eb, transparent)',
              'linear-gradient(90deg, transparent, #f97316, transparent)',
              'linear-gradient(90deg, transparent, #16a34a, transparent)',
              'linear-gradient(90deg, transparent, color-mix(in srgb, var(--accent) 72%, white 10%), transparent)',
            ][index]}
          />
        ))}
      </div>

      {snapshot.summary.divergenciasClassificacao > 0 ? (
        <div className="rounded-[18px] border border-amber-400/30 bg-amber-500/10 px-4 py-3 text-sm text-amber-700 dark:text-amber-200">
          {`${snapshot.summary.divergenciasClassificacao} CTOs possuem divergência entre a classificação recebida do n8n e a faixa calculada pela média RX.`}
        </div>
      ) : null}

      <div className="grid grid-cols-1 items-start gap-4 xl:grid-cols-[minmax(0,1.35fr)_minmax(360px,0.9fr)]">
        <div className="space-y-4">
          <SinalCtoMap
            rows={mapRows}
            loading={loading}
            isSyncing={isMapSyncing}
            onSelectCto={setCtoSelecionada}
          />

          {ctoSelecionada ? (
            <SinalCtoClientesPanel cto={ctoSelecionada} onClose={() => setCtoSelecionada(null)} />
          ) : null}

          <div className={panelClassName}>
            <div className="mb-3 flex flex-col gap-3">
              <div className="flex flex-col gap-2 sm:flex-row sm:items-end sm:justify-between">
                <div>
                  <h3 className="text-sm font-semibold text-[color:var(--text-primary)]">Média RX por cidade</h3>
                  <p className="mt-1 text-xs text-[color:var(--text-muted)]">
                    Agrupamento por cidade da ocupação. Cada id_cto contribui apenas uma vez para a média.
                  </p>
                </div>
                <span className="rounded-full border border-[color:var(--header-control-border-subtle)] px-2.5 py-1 text-[11px] text-[color:var(--text-muted)]">
                  Todas as cidades
                </span>
              </div>

              <label className="block rounded-[18px] border border-[color:var(--header-control-border-subtle)] bg-[color:var(--surface-strong)] px-3 py-2.5">
                <span className="text-[10px] font-semibold uppercase tracking-[0.14em] text-[color:var(--text-muted)]">
                  Buscar cidade
                </span>
                <span className="mt-2 flex min-h-[40px] items-center gap-2 rounded-[14px] border border-[color:var(--header-control-border-subtle)] bg-[color:var(--surface)] px-3">
                  <Search className="h-4 w-4 shrink-0 text-[color:var(--text-muted)]" />
                  <input
                    type="search"
                    value={citySearch}
                    onChange={(event) => setCitySearch(event.target.value)}
                    placeholder="Ex.: Santarém..."
                    className="h-9 min-w-0 flex-1 bg-transparent text-sm text-[color:var(--text-primary)] outline-none placeholder:text-[color:var(--text-muted)]/70"
                  />
                </span>
              </label>
            </div>
            <div className="w-full">
              {cityAverageRows.length > 0 ? (
                <div className="app-scroll-subtle max-h-[460px] overflow-y-auto pr-2">
                  <div
                    className="w-full"
                    style={{ height: Math.max(320, cityAverageRows.length * 42) }}
                  >
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart
                        data={cityAverageRows}
                        layout="vertical"
                        margin={{ top: 8, right: 18, left: isMobile ? 4 : 12, bottom: 8 }}
                      >
                        <CartesianGrid strokeDasharray="3 3" stroke="rgba(148,163,184,0.18)" />
                        <XAxis
                          type="number"
                          tick={{ fontSize: 11 }}
                          stroke="var(--text-muted)"
                          domain={['dataMin - 1', 'dataMax + 1']}
                          tickFormatter={(value) => `${rxFormatter.format(Number(value))}`}
                        />
                        <YAxis
                          type="category"
                        dataKey="label"
                        tick={{ fontSize: isMobile ? 9 : 11 }}
                        stroke="var(--text-muted)"
                        width={isMobile ? 78 : 108}
                      />
                        <Tooltip content={<AverageRxTooltip />} />
                        <Bar dataKey="media_rx" name="Média RX" radius={[0, 8, 8, 0]}>
                          {cityAverageRows.map((entry) => (
                            <Cell
                              key={entry.key}
                              fill={entry.media_rx < -25 ? '#ef4444' : entry.media_rx < -22 ? '#f59e0b' : '#2563eb'}
                            />
                          ))}
                        </Bar>
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                </div>
              ) : (
                <div className="flex min-h-[320px] items-center justify-center rounded-[18px] border border-dashed border-[color:var(--border)] px-4 text-center text-sm text-[color:var(--text-muted)]">
                  {snapshot.averages.cidade.length > 0
                    ? 'Nenhuma cidade encontrada para a busca.'
                    : 'Nenhuma cidade disponível para o recorte.'}
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="space-y-4">
          <div className={panelClassName}>
            <div className="mb-3 flex flex-col gap-2 sm:flex-row sm:items-end sm:justify-between">
              <div>
                <h3 className="text-sm font-semibold text-[color:var(--text-primary)]">CTOs por classificação</h3>
                <p className="mt-1 text-xs text-[color:var(--text-muted)]">
                  Total por classe usando IDs distintos. Soma atual: {integerFormatter.format(classificationTotal)}.
                  {snapshot.noDataClassificationCount > 0 ? ` Sem dados: ${integerFormatter.format(snapshot.noDataClassificationCount)}.` : ''}
                </p>
              </div>
              <span className="rounded-full border border-[color:var(--header-control-border-subtle)] px-2.5 py-1 text-[11px] text-[color:var(--text-muted)]">
                Power BI parity
              </span>
            </div>

            <div className="mb-4 h-[220px] w-full">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart
                  data={snapshot.classificationSummary}
                  layout="vertical"
                  margin={{ top: 8, right: 16, left: isMobile ? 18 : 34, bottom: 8 }}
                >
                  <CartesianGrid strokeDasharray="3 3" stroke="rgba(148,163,184,0.18)" />
                  <XAxis type="number" allowDecimals={false} tick={{ fontSize: 11 }} stroke="var(--text-muted)" />
                  <YAxis type="category" dataKey="label" tick={{ fontSize: 11 }} stroke="var(--text-muted)" width={isMobile ? 64 : 84} />
                  <Tooltip
                    contentStyle={chartTooltipStyle}
                    labelStyle={{ color: 'var(--text-primary)' }}
                    itemStyle={{ color: 'var(--text-primary)' }}
                    formatter={(value) => integerFormatter.format(Number(value || 0))}
                  />
                  <Bar
                    dataKey="count"
                    name="CTOs"
                    radius={[0, 8, 8, 0]}
                    onClick={(entry) => toggleFilter({
                      field: SINAL_FILTER_FIELDS.classificacao_sinal,
                      value: entry.key,
                      label: normalizeSignalFilterLabel(SINAL_FILTER_FIELDS.classificacao_sinal, entry.key),
                    })}
                  >
                    {snapshot.classificationSummary.map((entry) => (
                      <Cell key={entry.key} fill={entry.color} className="cursor-pointer" />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>

            <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
              {snapshot.classificationSummary.map((item) => {
                const isActive = activeClassifications.includes(item.key);

                return (
                  <button
                    key={item.key}
                    type="button"
                    onClick={() => toggleFilter({
                      field: SINAL_FILTER_FIELDS.classificacao_sinal,
                      value: item.key,
                      label: normalizeSignalFilterLabel(SINAL_FILTER_FIELDS.classificacao_sinal, item.key),
                    })}
                    className={`min-h-[86px] rounded-[16px] border px-3 py-3 text-left transition-all ${
                      isActive
                        ? 'border-[color:var(--accent)] bg-[color:var(--surface)]/96 shadow-[0_0_0_1px_color-mix(in_srgb,var(--accent)_22%,transparent)]'
                        : 'border-[color:var(--border)] bg-[color:var(--surface-strong)]/80 hover:border-[color:var(--accent)]/38'
                    }`}
                  >
                    <div className="flex items-center gap-2">
                      <span
                        className="inline-flex h-2.5 w-2.5 shrink-0 rounded-full"
                        style={{ backgroundColor: item.color }}
                        aria-hidden="true"
                      />
                      <span className="truncate text-xs font-semibold text-[color:var(--text-primary)]">
                        {item.label}
                      </span>
                    </div>
                    <div className="mt-3 flex items-end justify-between gap-2">
                      <p className="text-2xl font-semibold leading-none" style={{ color: item.color }}>
                        {integerFormatter.format(item.count)}
                      </p>
                      <p className="pb-0.5 text-[10px] font-semibold uppercase tracking-[0.12em] text-[color:var(--text-muted)]">
                        {item.percent.toFixed(1)}%
                      </p>
                    </div>
                    {isActive ? (
                      <p className="mt-2 text-[10px] font-semibold uppercase tracking-[0.12em] text-[color:var(--accent)]">
                        Filtro ativo
                      </p>
                    ) : null}
                  </button>
                );
              })}
            </div>
          </div>

          <div className={panelClassName}>
            <div className="mb-3 flex flex-col gap-3">
              <div className="flex flex-col gap-2 sm:flex-row sm:items-end sm:justify-between">
                <div>
                  <h3 className="text-sm font-semibold text-[color:var(--text-primary)]">Média RX por POP</h3>
                  <p className="mt-1 text-xs text-[color:var(--text-muted)]">
                    Média simples por CTO. Registros sem POP ficam fora deste agrupamento.
                  </p>
                </div>
                <span className="rounded-full border border-[color:var(--header-control-border-subtle)] px-2.5 py-1 text-[11px] text-[color:var(--text-muted)]">
                  Todos os POPs
                </span>
              </div>

              <label className="block rounded-[18px] border border-[color:var(--header-control-border-subtle)] bg-[color:var(--surface-strong)] px-3 py-2.5">
                <span className="text-[10px] font-semibold uppercase tracking-[0.14em] text-[color:var(--text-muted)]">
                  Buscar POP
                </span>
                <span className="mt-2 flex min-h-[40px] items-center gap-2 rounded-[14px] border border-[color:var(--header-control-border-subtle)] bg-[color:var(--surface)] px-3">
                  <Search className="h-4 w-4 shrink-0 text-[color:var(--text-muted)]" />
                  <input
                    type="search"
                    value={popSearch}
                    onChange={(event) => setPopSearch(event.target.value)}
                    placeholder="Ex.: PA_ATM_AR03..."
                    className="h-9 min-w-0 flex-1 bg-transparent text-sm text-[color:var(--text-primary)] outline-none placeholder:text-[color:var(--text-muted)]/70"
                  />
                </span>
              </label>
            </div>
            <div className="w-full">
              {popAverageRows.length > 0 ? (
                <div className="app-scroll-subtle max-h-[460px] overflow-y-auto pr-2">
                  <div
                    className="w-full"
                    style={{ height: Math.max(320, popAverageRows.length * 42) }}
                  >
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={popAverageRows} layout="vertical" margin={{ top: 8, right: 16, left: isMobile ? 20 : 64, bottom: 8 }}>
                        <CartesianGrid strokeDasharray="3 3" stroke="rgba(148,163,184,0.18)" />
                        <XAxis type="number" tick={{ fontSize: 11 }} stroke="var(--text-muted)" domain={['dataMin - 1', 'dataMax + 1']} />
                        <YAxis type="category" dataKey="label" tick={{ fontSize: isMobile ? 9 : 11 }} stroke="var(--text-muted)" width={isMobile ? 72 : 120} />
                        <Tooltip content={<AverageRxTooltip />} />
                        <Bar dataKey="media_rx" name="Média RX" radius={[0, 8, 8, 0]} fill="#2563eb" />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                </div>
              ) : (
                <div className="flex min-h-[280px] items-center justify-center rounded-[18px] border border-dashed border-[color:var(--border)] px-4 text-center text-sm text-[color:var(--text-muted)]">
                  {snapshot.averages.pop.length > 0
                    ? 'Nenhum POP encontrado para a busca.'
                    : 'Nenhum POP disponível para o recorte.'}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const SinalCtoDashboard = ({ isMobile = false }) => (
  <SinalCtoDataProvider>
    <SinalCtoDashboardContent isMobile={isMobile} />
  </SinalCtoDataProvider>
);

export default SinalCtoDashboard;
